import { motion } from "framer-motion";
import { SANDALWOOD, SAFFRON, IMAGES } from "../../data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const SectionHead = ({ label, title, italic }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    className="mb-16 max-w-3xl"
  >
    <p className="font-sans-lux text-xs uppercase tracking-[0.3em] text-[#D49A44] mb-6">{label}</p>
    <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#F4EFE6]">
      {title} <span className="italic text-[#D49A44]">{italic}</span>
    </h2>
  </motion.div>
);

export const Sandalwood = () => {
  return (
    <section
      id="sandalwood"
      data-testid="sandalwood-section"
      className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-36"
    >
      <SectionHead label="The Wood" title="Sandalwood," italic="in every form." />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Feature image (tall) */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="md:col-span-5 md:row-span-2 group relative overflow-hidden border border-[#2C2621] min-h-[360px] md:min-h-[560px]"
        >
          <img
            src={IMAGES.sandalwoodIncense}
            alt="Sandalwood incense and powder"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-[#12100E]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8">
            <p className="font-serif-lux text-3xl text-[#F4EFE6]">Santalum album</p>
            <p className="font-sans-lux text-xs uppercase tracking-[0.2em] text-[#D49A44] mt-2">
              The royal tree of Mysore lineage
            </p>
          </div>
        </motion.div>

        {/* Product cards */}
        {SANDALWOOD.map((p, i) => (
          <motion.div
            key={p.name}
            custom={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="md:col-span-3 border border-[#2C2621] p-8 hover:border-[#D49A44]/50 transition-colors duration-500 flex flex-col justify-between min-h-[240px]"
          >
            <span className="font-serif-lux text-5xl text-[#D49A44]/20">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <p className="font-sans-lux text-[10px] uppercase tracking-[0.2em] text-[#7C756B] mb-2">
                {p.tag}
              </p>
              <h3 className="font-serif-lux text-2xl text-[#F4EFE6] mb-3">{p.name}</h3>
              <p className="font-sans-lux text-sm text-[#B0A89D] leading-relaxed">{p.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const Saffron = () => {
  return (
    <section
      id="saffron"
      data-testid="saffron-section"
      className="relative overflow-hidden bg-[#1A1714] border-y border-[#2C2621]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-36">
        <SectionHead label="The Thread" title="Saffron," italic="the crimson gold." />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {SAFFRON.map((p, i) => (
            <motion.div
              key={p.name}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="group relative overflow-hidden border border-[#2C2621] min-h-[440px]"
            >
              <img
                src={i === 0 ? IMAGES.saffronThreads : IMAGES.saffronMacro}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-[#12100E]/50 to-[#12100E]/10" />
              <div className="relative h-full flex flex-col justify-end p-10">
                <p className="font-sans-lux text-[10px] uppercase tracking-[0.25em] text-[#D49A44] mb-3">
                  {p.tag}
                </p>
                <h3 className="font-serif-lux text-4xl md:text-5xl text-[#F4EFE6] mb-4">{p.name}</h3>
                <p className="font-sans-lux text-sm md:text-base text-[#B0A89D] leading-relaxed max-w-md">
                  {p.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
